import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonList } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { listMeetingsPage } from '@/shared/api/meetings';
import { formatKRW } from '@/shared/lib/money';

/**
 * 결제 알림 자동입력 진입점 (Android 래퍼 딥링크 대상).
 * /expense-suggest?amount=15000&desc=스타벅스
 * 감지된 결제를 어느 모임의 비용으로 올릴지 사용자가 직접 고른다.
 * 모임을 고르면 비용 입력 폼에 금액·내용이 프리필된 상태로 이동한다.
 */
export default function ExpenseSuggestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const amount = searchParams.get('amount') ?? '';
  const desc = searchParams.get('desc') ?? '';
  const isValidAmount = /^\d+$/.test(amount);

  const meetingsQ = useInfiniteQuery({
    queryKey: ['meetings'],
    queryFn: ({ pageParam }) => listMeetingsPage(pageParam, 20),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const items = (meetingsQ.data?.pages.flatMap((p) => p.items) ?? []).filter(
    (m) => m.status === 'ONGOING' || m.status === 'SETTLING' || m.status === 'CONFIRMED',
  );

  function pick(meetingId: string) {
    const params = new URLSearchParams();
    if (isValidAmount) params.set('amount', amount);
    if (desc) params.set('desc', desc);
    navigate(`/meetings/${meetingId}/expense?${params.toString()}`);
  }

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-section font-semibold tracking-tight-feature text-ink">
          결제 내역을 정산에 올릴까요?
        </h1>
        <p className="mt-1 text-body text-ink-muted">
          휴대폰 알림에서 결제가 감지됐어요. 올릴 모임을 골라주세요.
        </p>
      </header>

      {isValidAmount && (
        <Card className="!p-6">
          <p className="text-small font-medium text-ink-muted">
            감지된 결제
          </p>
          <p className="mt-1 text-section font-semibold tracking-tight-feature text-ink">
            {formatKRW(Number(amount))}
          </p>
          {desc && <p className="mt-1 text-body text-ink-muted">{desc}</p>}
        </Card>
      )}

      {meetingsQ.isLoading && <SkeletonList count={3} />}
      {meetingsQ.isError && (
        <ErrorState title="모임을 불러오지 못했어요" onRetry={() => meetingsQ.refetch()} />
      )}

      {meetingsQ.data && items.length === 0 && (
        <EmptyState
          title="진행 중인 모임이 없어요"
          description="확정·진행·정산 중인 모임에만 비용을 올릴 수 있어요."
        />
      )}

      {items.length > 0 && (
        <ul className="space-y-2.5" aria-label="비용을 올릴 모임 선택">
          {items.map((m) => (
            <li key={m.id}>
              <button type="button" onClick={() => pick(m.id)} className="block w-full text-left">
                <Card className="transition-shadow duration-150 hover:shadow-card-hover">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-ui font-medium text-ink">{m.title}</p>
                      {m.region && (
                        <p className="mt-0.5 text-small text-ink-muted">{m.region}</p>
                      )}
                    </div>
                    <StatusBadge status={m.status} />
                  </div>
                </Card>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
